import React, { useState, useEffect } from "react";
import { HeaderComponent, TableComponent, BodyComponent, ActionComponent, trans, Switch } from '@comps/components';
import { Tag } from 'antd';
import { makeRequest, makeRequestStateless } from '@utils/helpers';
import { getUsersReq, changeStatusReq, deleteUserReq, getFilters } from './requests';
import { updateById, deleteById, createSelectList } from '@comps/commonFunctions';
import CreateUser from "./components/CreateUser";
import UpdateUser from "./components/UpdateUser";
import { getRolesReq } from "../roles/requests";

const langKey = 'UserManagement.users';

const pageConfig = {
  headers: {
    title: trans(`${langKey}.moduleName`),
    breadcrumb: [
      {
        name: trans(`${langKey}.moduleName`),
        path: "/user-management/users"
      }
    ]
  }
}

const module = 'users';

const IndexUsers = () => {
  const [dataSource, setDataSource] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loader, setLoader] = useState(false);
  const [filters, setFilters] = useState({});
  const [childComponent, setChildComponent] = useState(null);
  const [roles, setRoles] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });


  useEffect(() => {
    getRolesData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Deliberately stateless. Roles only fill the Role filter and the
   * create/edit drawers — they are not the table's data, and they were
   * fetched through the table's loader alongside the users list. Two
   * requests racing one flag meant that whenever roles answered first it
   * switched the loader off under a users request still in flight, and
   * the table showed "No users yet" for a beat before the list arrived.
   */
  const getRolesData = () => {
    makeRequestStateless(getRolesReq, null, onRoleSuccess, onError);
  }

  const onRoleSuccess = (res) => {
    setRoles(createSelectList(res.data));
  }

  useEffect(() => {
    getUsersData();
  }, [pagination, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const getUsersData = () => {
    let payload = {
      start: (pagination.current - 1) * pagination.pageSize,
      length: pagination.pageSize,
      sort_name: pagination.sortName,
      sort_type: pagination.sortType,
      filters
    };
    makeRequest(setLoader, getUsersReq, payload, onSuccess, onError);
  }

  const onError = (error) => {
    console.log(error);
  }

  const onSuccess = (data) => {
    setDataSource(data.data);
    setTotalRecords(data.totalRecords);
  }


  const handleTableChange = (page, fil, sorter) => {
    let payload = {
      ...pagination,
      current: page.current,
      pageSize: page.pageSize,
      sortName: sorter.field || 'id',
      sortType: sorter.order === 'ascend' ? 'asc' : 'desc',
    };
    setPagination(payload);
  }


  const onChangeStatus = (data, action) => {
    let payload = {
      id: data.id,
      status: action
    };
    makeRequest(setLoader, changeStatusReq, payload, onChangeStatusSuccess, onChangeStatusError);
  }

  const onChangeStatusSuccess = (data) => {
    let index = dataSource.findIndex(each => each.id === data.id);
    dataSource[index].is_active = data.is_active;
    setDataSource([...dataSource]);
  }

  const onChangeStatusError = (error) => {
    console.log(error);
  }

  const onCreate = () => {
    setChildComponent(<CreateUser roles={ roles } onCreated={ onCreated } onClosed={ onClosed } />);
  }

  const onCreated = (data) => {
    setDataSource([data, ...dataSource]);
    setChildComponent(null);
  }


  const onEdit = (record) => {
    setChildComponent(<UpdateUser roles={ roles } data={ record } onUpdated={ onUpdated } onClosed={ onClosed } />);
  }

  const onUpdated = (data) => {
    setDataSource([...updateById(dataSource, data)]);
    setChildComponent(null);
  }

  const onDelete = (record) => {
    makeRequest(setLoader, deleteUserReq, record.id, onDeleteSuccess, Function);
  }

  const onDeleteSuccess = (data) => {
    setDataSource([...deleteById(dataSource, data)]);
    // notifySuccess(trans(`common.deleteSuccess`));
  }

  const onClosed = () => {
    setChildComponent(null);
  }

  const columns = [
    {
      title: trans(`common.name`),
      key: "identity",
      render: (record) => (
        <div className="identity-cell">
          <span className="identity-avatar">
            { (record.name || "?").charAt(0).toUpperCase() }
          </span>
          <span className="identity-meta">
            <span className="identity-name">{ record.name }</span>
            <span className="identity-sub">{ record.email }</span>
          </span>
        </div>
      ),
    },
    {
      title: trans(`common.role`),
      key: "role_name",
      render: (record) => <Tag className="soft-chip">{ record.role_name }</Tag>,
    },
    {
      title: trans(`common.active`),
      key: "is_active",
      width: 110,
      render: (data) => (
        <Switch onChange={ (action) => onChangeStatus(data, action) } checked={ data.is_active } />
      ),
    },
    {
      title: trans(`common.actions`),
      key: "actions",
      align: "right",
      width: 110,
      render: (record) => (
        <ActionComponent module={ module } each={ record } onEdit={ onEdit } onDelete={ onDelete } />
      ),
    },
  ];

  const filterConfig = {
    availableFilters: availableFilters,
    filters: filters,
    setFilters: setFilters,
    getFilters: getFilters
  }

  return (
    <>
      { childComponent }
      <HeaderComponent
        headers={ pageConfig.headers }
        description="Everyone who can sign in, and what they're allowed to do."
      />
      <BodyComponent flush module={ module } filterConfig={ filterConfig } onCreate={ onCreate }>
        <TableComponent
          loader={ loader }
          columns={ columns }
          dataSource={ dataSource }
          emptyState={ {
            icon: "tabler:users",
            title: "No users match these filters",
            description: "Clear the filters, or add a user to get them started.",
          } }
          pagination={ { ...pagination, total: totalRecords } }
          onChange={ handleTableChange }
        />
      </BodyComponent>
    </>
  )
}


const availableFilters = [
  {
    key: 'name',
    placeholder: 'User Name',
    type: 'text',
  },
  {
    key: 'email',
    placeholder: 'User Email',
    type: 'text',
  },
  {
    key: 'is_active',
    placeholder: 'Select Status',
    type: 'select',
    data_key: 'actives'
  },
  {
    key: 'role_id',
    placeholder: 'Select Role',
    type: 'select',
    data_key: 'all_roles',
  }
];

export default IndexUsers